import React, { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useSearchParams } from 'react-router-dom';
import { Search, X, ArrowRight, Plus } from 'lucide-react';
import { projects as projectsTr } from '../data/projects';
import { projectsEn } from '../data/projects.en';
import { useLanguage } from '../context/LanguageContext';
import { tr } from '../locales/tr';
import { en } from '../locales/en';

const ITEMS_PER_PAGE = 12;

const Projects: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { language } = useLanguage();
  const t = language === 'TR' ? tr.projects : en.projects;
  const currentProjects = language === 'TR' ? projectsTr : projectsEn;
  const allLabel = t.all;

  const [visibleCount, setVisibleCount] = useState(ITEMS_PER_PAGE);

  // Read search parameters
  const activeCategory = searchParams.get('kategori') || allLabel;
  const searchQuery = searchParams.get('q') || '';
  const activeSort = searchParams.get('sirala') || 'featured';

  // 1. Dynamically compile list of categories that have at least one project
  const categoriesList = useMemo(() => {
    const list = new Set<string>();
    currentProjects.forEach(p => {
      p.categories.forEach(cat => list.add(cat));
    });
    return [allLabel, ...Array.from(list).sort()];
  }, [currentProjects, allLabel]);

  // 2. Filter projects based on search query and selected category
  const filteredProjects = useMemo(() => {
    return currentProjects.filter(p => {
      const matchesCategory =
        activeCategory === allLabel ||
        activeCategory === 'Tümü' ||
        activeCategory === 'All' ||
        p.categories.includes(activeCategory);

      const searchLower = searchQuery.toLowerCase();
      const matchesSearch =
        searchLower === '' ||
        p.title.toLowerCase().includes(searchLower) ||
        p.client.toLowerCase().includes(searchLower) ||
        p.shortDescription.toLowerCase().includes(searchLower) ||
        (p.fullDescription && p.fullDescription.toLowerCase().includes(searchLower));

      return matchesCategory && matchesSearch;
    });
  }, [currentProjects, activeCategory, searchQuery, allLabel]);

  // 3. Sort filtered projects
  const sortedProjects = useMemo(() => {
    const sorted = [...filteredProjects];
    if (activeSort === 'newest') {
      sorted.sort((a, b) => {
        const yearA = a.year || 0;
        const yearB = b.year || 0;
        return yearB - yearA;
      });
    } else if (activeSort === 'alpha') {
      sorted.sort((a, b) => a.title.localeCompare(b.title, language === 'TR' ? 'tr' : 'en'));
    } else {
      // Featured: featured projects first, then by title
      sorted.sort((a, b) => {
        if (a.featured && !b.featured) return -1;
        if (!a.featured && b.featured) return 1;
        return a.title.localeCompare(b.title, language === 'TR' ? 'tr' : 'en');
      });
    }
    return sorted;
  }, [filteredProjects, activeSort, language]);

  // Paginated slice
  const displayedProjects = useMemo(() => {
    return sortedProjects.slice(0, visibleCount);
  }, [sortedProjects, visibleCount]);

  const handleLoadMore = () => {
    setVisibleCount(prev => prev + ITEMS_PER_PAGE);
  };

  // Update URL params helper
  const updateParam = (key: string, value: string) => {
    const newParams = new URLSearchParams(searchParams);
    if (value && value !== allLabel) {
      newParams.set(key, value);
    } else {
      newParams.delete(key);
    }
    setSearchParams(newParams);
    setVisibleCount(ITEMS_PER_PAGE);
  };

  const handleClearFilters = () => {
    setSearchParams({});
    setVisibleCount(ITEMS_PER_PAGE);
  };

  const isTR = language === 'TR';

  return (
    <div className="pt-28 pb-20 min-h-screen bg-[#030811]">
      <Helmet>
        <title>{isTR ? "Projelerimiz & Portföy - WE24 Agency" : "Our Projects & Portfolio - WE24 Agency"}</title>
        <meta
          name="description"
          content={
            isTR
              ? "Sinematik reklam filmleri, canlı yayın reji, etkinlik aftermovie ve dijital platformlar için ürettiğimiz seçkin vaka analizleri."
              : "Discover our portfolio of cinematic commercial films, multi-camera live broadcasts, VFX, and bespoke software."
          }
        />
        <link rel="canonical" href={isTR ? "https://www.we24agency.com/projeler/" : "https://www.we24agency.com/en/projects/"} />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Header Title Section */}
        <div className="mb-10 text-center sm:text-left">
          <span className="text-xs uppercase tracking-[0.25em] text-[#ff7a63] font-bold block mb-2">
            {isTR ? "Seçili İşler & Vaka Analizleri" : "Selected Works & Case Studies"}
          </span>
          <h1 className="text-3xl sm:text-5xl font-extrabold text-white tracking-tight">
            {isTR ? "Projelerimiz" : "Our Portfolio"}
          </h1>
          <p className="text-sm sm:text-base text-zinc-400 mt-2 max-w-2xl">
            {isTR
              ? "Ulusal ve küresel markalar için hayata geçirdiğimiz sinematik prodüksiyon, canlı yayın ve dijital projeler."
              : "Cinematic commercial films, live broadcasts, and digital experiences crafted for global partners."}
          </p>
        </div>

        {/* Filter & Search Bar */}
        <div className="bg-[#071927]/80 border border-white/10 backdrop-blur-md rounded-2xl p-4 sm:p-5 mb-10 shadow-xl">
          <div className="flex flex-col md:flex-row gap-4 justify-between items-stretch md:items-center">
            {/* Search Input */}
            <div className="relative flex-1">
              <Search className="w-4 h-4 text-zinc-400 absolute left-3.5 top-1/2 -translate-y-1/2" aria-hidden="true" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => updateParam('q', e.target.value)}
                placeholder={isTR ? "Proje veya marka ara..." : "Search project or brand..."}
                className="w-full pl-10 pr-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-xs sm:text-sm text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#ff7a63] transition-all"
                aria-label={isTR ? "Proje veya marka arama" : "Search projects"}
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => updateParam('q', '')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-white"
                  aria-label={isTR ? "Aramayı temizle" : "Clear search"}
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            {/* Sort Selector */}
            <div className="flex items-center gap-2">
              <span className="text-xs text-zinc-400 font-medium whitespace-nowrap hidden sm:inline">
                {isTR ? "Sıralama:" : "Sort:"}
              </span>
              <select
                value={activeSort}
                onChange={(e) => updateParam('sirala', e.target.value)}
                className="py-2.5 px-3 bg-white/5 border border-white/10 rounded-xl text-xs sm:text-sm text-white focus:outline-none focus:ring-2 focus:ring-[#ff7a63] cursor-pointer"
                aria-label={isTR ? "Sıralama ölçütü" : "Sort order"}
              >
                <option value="featured" className="bg-zinc-900 text-white">{isTR ? "Öne Çıkanlar" : "Featured"}</option>
                <option value="newest" className="bg-zinc-900 text-white">{isTR ? "En Yeniler" : "Newest"}</option>
                <option value="alpha" className="bg-zinc-900 text-white">{isTR ? "Alfabetik (A-Z)" : "Alphabetical"}</option>
              </select>
            </div>
          </div>

          {/* Category Chips with Accessible aria-pressed */}
          <div className="flex items-center gap-2 overflow-x-auto pt-4 mt-3 border-t border-white/5 no-scrollbar">
            {categoriesList.map((cat) => {
              const isSelected = activeCategory === cat || (activeCategory === allLabel && cat === allLabel);
              return (
                <button
                  key={cat}
                  type="button"
                  onClick={() => updateParam('kategori', cat === allLabel ? '' : cat)}
                  aria-pressed={isSelected}
                  className={`px-3.5 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all flex items-center gap-1.5 focus-visible:ring-2 focus-visible:ring-[#ff7a63] ${
                    isSelected
                      ? 'bg-white text-zinc-950 shadow-md font-bold scale-105'
                      : 'bg-white/5 text-zinc-300 hover:bg-white/10 hover:text-white border border-white/5'
                  }`}
                >
                  <span>{cat}</span>
                  {isSelected && <span className="w-1.5 h-1.5 rounded-full bg-[#ff7a63]" aria-hidden="true" />}
                </button>
              );
            })}
          </div>
        </div>

        {/* Project Cards Grid */}
        {displayedProjects.length > 0 ? (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
              {displayedProjects.map((project) => {
                const detailUrl = isTR ? `/projeler/${project.slug}` : `/en/projects/${project.slug}`;
                return (
                  <article
                    key={project.id || project.slug}
                    className="group bg-[#071927]/60 border border-white/10 rounded-2xl overflow-hidden hover:border-[#ff7a63]/50 transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl flex flex-col justify-between"
                  >
                    {/* Cover Media Container with fixed aspect-ratio placeholder */}
                    <Link to={detailUrl} className="relative block aspect-[16/10] overflow-hidden bg-zinc-950">
                      {project.coverImage ? (
                        <img
                          src={project.coverImage}
                          alt={project.title}
                          loading="lazy"
                          decoding="async"
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 filter brightness-[0.9] group-hover:brightness-100"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-zinc-900 text-zinc-600 text-xs">
                          WE24 Showcase
                        </div>
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-[#071927] via-transparent to-transparent opacity-80" />
                      
                      {/* Top Category Badge */}
                      <div className="absolute top-3 left-3">
                        <span className="px-2.5 py-1 bg-black/70 backdrop-blur-md rounded-md text-[10px] font-bold uppercase tracking-wider text-[#ff7a63] border border-white/10">
                          {project.categories[0] || (isTR ? 'Prodüksiyon' : 'Production')}
                        </span>
                      </div>
                    </Link>

                    {/* Content */}
                    <div className="p-5 flex-1 flex flex-col justify-between">
                      <div>
                        <div className="flex items-center justify-between text-xs text-zinc-400 mb-2">
                          <span className="font-semibold text-zinc-300">{project.client}</span>
                          <span>{project.year || 2024}</span>
                        </div>
                        <h2 className="text-lg font-bold text-white group-hover:text-[#ff7a63] transition-colors leading-snug">
                          <Link to={detailUrl}>
                            {project.title}
                          </Link>
                        </h2>
                        <p className="text-xs text-zinc-400 mt-2 line-clamp-2 leading-relaxed">
                          {project.shortDescription}
                        </p>
                      </div>

                      {/* Footer Link */}
                      <div className="pt-4 mt-4 border-t border-white/5 flex items-center justify-between">
                        <Link
                          to={detailUrl}
                          className="text-xs font-bold text-white group-hover:text-[#ff7a63] inline-flex items-center gap-1.5 transition-colors focus-visible:ring-2 focus-visible:ring-[#ff7a63] rounded"
                        >
                          <span>{isTR ? "Vaka Analizini İncele" : "View Case Study"}</span>
                          <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                        </Link>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>

            {/* Pagination / Load More */}
            {visibleCount < sortedProjects.length && (
              <div className="mt-12 text-center flex flex-col items-center">
                <p className="text-xs text-zinc-400 mb-3">
                  {isTR
                    ? `${displayedProjects.length} / ${sortedProjects.length} proje gösteriliyor`
                    : `Showing ${displayedProjects.length} of ${sortedProjects.length} projects`}
                </p>
                <button
                  type="button"
                  onClick={handleLoadMore}
                  className="px-8 py-3.5 bg-white hover:bg-zinc-200 text-zinc-950 font-bold rounded-full text-xs uppercase tracking-wider transition-all shadow-lg hover:scale-105 inline-flex items-center gap-2 focus-visible:ring-2 focus-visible:ring-[#ff7a63]"
                >
                  <Plus className="w-4 h-4" />
                  <span>{isTR ? "Daha Fazla Proje Yükle" : "Load More Projects"}</span>
                </button>
              </div>
            )}
          </>
        ) : (
          /* Empty State */
          <div className="py-20 text-center bg-[#071927]/40 border border-white/10 rounded-3xl p-8 max-w-lg mx-auto">
            <h2 className="text-xl font-bold text-white mb-2">
              {isTR ? "Eşleşen Proje Bulunamadı" : "No Matching Projects Found"}
            </h2>
            <p className="text-xs text-zinc-400 mb-6 leading-relaxed">
              {isTR
                ? "Seçtiğiniz filtreler veya arama terimiyle eşleşen proje kaydı bulunamadı. Filtreleri sıfırlayarak tüm portföyü görüntüleyebilirsiniz."
                : "No projects match your current filter criteria. You can clear filters to browse the full portfolio."}
            </p>
            <button
              type="button"
              onClick={handleClearFilters}
              className="px-6 py-2.5 bg-white text-zinc-950 text-xs font-bold rounded-full uppercase tracking-wider hover:bg-zinc-200 transition-all focus-visible:ring-2 focus-visible:ring-[#ff7a63]"
            >
              {isTR ? "Filtreleri Sıfırla" : "Reset Filters"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Projects;
